import { useState, useEffect, useRef } from "react";
import { uploadFile } from "../../../../../../requests/funcitons";
import "./VideoPicker.css";

export default function VideoPicker({ value, onChange, style }) {
    const [isOpen, setIsOpen] = useState(false);
    const [mode, setMode] = useState("url"); // "url" ou "file"
    const [urlInput, setUrlInput] = useState("");
    const [previewUrl, setPreviewUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [error, setError] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isPlaying, setIsPlaying] = useState(false);
    const fileInputRef = useRef(null);
    const videoRef = useRef(null);

    useEffect(() => {
        if (typeof value === "string" && value) {
            setPreviewUrl(value);
            setUrlInput(value);
        }
    }, [value]);

    const handleUrlChange = (e) => {
        setUrlInput(e.target.value);
        setError(null);
    };

    const handleUrlApply = () => {
        if (!urlInput.trim()) {
            setError("Veuillez entrer une URL");
            return;
        }
        setPreviewUrl(urlInput);
        onChange(urlInput);
        setIsOpen(false);
    };

    const handleFileSelect = async (file) => {
        if (!file) return;

        if (!file.type.startsWith("video/")) {
            setError("Le fichier doit être une vidéo");
            return;
        }

        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
            setError("La vidéo ne doit pas dépasser 100MB");
            return;
        }

        setError(null);
        setIsUploading(true);
        const objectUrl = URL.createObjectURL(file);
        setPreviewUrl(objectUrl);

        try {
            const url = await uploadFile(file);
            setPreviewUrl(url);
            setUrlInput(url);
            onChange(url);
            setIsOpen(false);
        } catch {
            setPreviewUrl(typeof value === "string" ? value : null);
            setError("Échec de l'upload de la vidéo");
        } finally {
            URL.revokeObjectURL(objectUrl);
            setIsUploading(false);
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files[0];
        if (file) {
            handleFileSelect(file);
        }
    };

    const handleClear = () => {
        setPreviewUrl(null);
        setUrlInput("");
        onChange(null);
        setIsOpen(false);
    };

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const getDisplayValue = () => {
        if (typeof value === "string" && value) {
            return value.length > 25 ? value.substring(0, 25) + "..." : value;
        }
        return "Aucune vidéo";
    };

    return (
        <div className="video-picker-container">
            <button
                type="button"
                className="video-picker-trigger"
                onClick={() => setIsOpen(true)}
                style={{marginTop: 6}}
            >
                <span className="video-picker-preview">
                    {previewUrl ? (
                        <video src={previewUrl} muted />
                    ) : (
                        <span className="video-picker-placeholder">🎬</span>
                    )}
                    <span className="video-picker-play-icon">▶</span>
                </span>
                <span className="video-picker-value">{getDisplayValue()}</span>
            </button>

            {/* Modal */}
            {isOpen && (
                <>
                    <div
                        className="video-picker-overlay"
                        onClick={() => setIsOpen(false)}
                    />
                    <div className="video-picker-modal">
                        {/* Header */}
                        <div className="video-picker-header">
                            <h3 className="video-picker-title">🎬 Sélecteur de vidéo</h3>
                            <button
                                type="button"
                                className="video-picker-close"
                                onClick={() => setIsOpen(false)}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Onglets */}
                        <div className="video-picker-tabs">
                            <button
                                type="button"
                                className={`video-picker-tab ${mode === "url" ? "active" : ""}`}
                                onClick={() => setMode("url")}
                            >
                                <span className="tab-icon">🔗</span>
                                URL
                            </button>
                            <button
                                type="button"
                                className={`video-picker-tab ${mode === "file" ? "active" : ""}`}
                                onClick={() => setMode("file")}
                            >
                                <span className="tab-icon">📁</span>
                                Fichier
                            </button>
                        </div>

                        {/* Contenu */}
                        <div className="video-picker-content">
                            {/* Prévisualisation */}
                            <div className="video-picker-preview-large">
                                {previewUrl ? (
                                    <div className="video-preview-wrapper">
                                        <video
                                            ref={videoRef}
                                            src={previewUrl}
                                            onEnded={() => setIsPlaying(false)}
                                            onClick={togglePlay}
                                        />
                                        <button
                                            type="button"
                                            className={`video-play-button ${isPlaying ? "playing" : ""}`}
                                            onClick={togglePlay}
                                        >
                                            {isPlaying ? "⏸" : "▶"}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="video-picker-no-preview">
                                        <span className="no-preview-icon">🎥</span>
                                        <span className="no-preview-text">Aucune vidéo sélectionnée</span>
                                    </div>
                                )}
                            </div>

                            {/* Mode URL */}
                            {mode === "url" && (
                                <div className="video-picker-section">
                                    <label className="video-picker-label">URL de la vidéo</label>
                                    <div className="video-picker-url-input">
                                        <input
                                            type="text"
                                            value={urlInput}
                                            onChange={handleUrlChange}
                                            placeholder="https://exemple.com/video.mp4"
                                            className="video-picker-input-text"
                                        />
                                        <button
                                            type="button"
                                            className="video-picker-preview-btn"
                                            onClick={() => setPreviewUrl(urlInput)}
                                            title="Prévisualiser"
                                        >
                                            👁️
                                        </button>
                                    </div>
                                    <div className="video-picker-url-hint">
                                        Formats supportés : MP4, WebM, OGG
                                    </div>
                                </div>
                            )}

                            {/* Mode Fichier */}
                            {mode === "file" && (
                                <div className="video-picker-section">
                                    <label className="video-picker-label">Importer une vidéo</label>
                                    <div
                                        className={`video-picker-dropzone ${isDragging ? "dragging" : ""} ${isUploading ? "uploading" : ""}`}
                                        onDragOver={handleDragOver}
                                        onDragLeave={handleDragLeave}
                                        onDrop={handleDrop}
                                        onClick={() => !isUploading && fileInputRef.current?.click()}
                                    >
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="video/*"
                                            onChange={(e) => handleFileSelect(e.target.files[0])}
                                            style={{ display: "none" }}
                                        />
                                        <div className="dropzone-content">
                                            <span className="dropzone-icon">
                                                {isDragging ? "📥" : "🎞️"}
                                            </span>
                                            <span className="dropzone-text">
                                                {isUploading
                                                    ? "Upload en cours..."
                                                    : isDragging
                                                    ? "Déposez la vidéo ici"
                                                    : "Glissez-déposez une vidéo ou cliquez pour parcourir"}
                                            </span>
                                            <span className="dropzone-hint">
                                                MP4, WebM, OGG • Max 100MB
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Message d'erreur */}
                            {error && (
                                <div className="video-picker-error">
                                    <span>⚠️</span> {error}
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="video-picker-footer">
                            {previewUrl && (
                                <button
                                    type="button"
                                    className="video-picker-btn video-picker-btn-clear"
                                    onClick={handleClear}
                                >
                                    🗑️ Supprimer
                                </button>
                            )}
                            <div className="video-picker-footer-right">
                                <button
                                    type="button"
                                    className="video-picker-btn video-picker-btn-cancel"
                                    onClick={() => setIsOpen(false)}
                                >
                                    Annuler
                                </button>
                                {mode === "url" && (
                                    <button
                                        type="button"
                                        className="video-picker-btn video-picker-btn-apply"
                                        onClick={handleUrlApply}
                                    >
                                        Appliquer
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

